<script setup lang="ts">
/**
 * Настройки → Интеграции — макет «Настройки · Интеграции» (198:4697), DEV-1518.
 *
 * Экран отвечает на один вопрос: как подключить агента к трекеру. Порядок на
 * нём — порядок действий: сначала ключ, потом команда. В первой версии было
 * наоборот, и человек копировал команду, которую нечем заполнить.
 *
 * Нижний блок «что агент делает» — не украшение: ключ живёт до года и работает
 * от имени человека, поэтому список того, чего агент не может, стоит рядом с
 * кнопкой выпуска, а не в документации.
 *
 * Кнопки «Сохранить» здесь нет намеренно: ключ выпускается диалогом, отзыв
 * применяется сразу. Откатывать черновиком нечего — как на экране репозиториев.
 *
 * Раздел личный, а не проектный: ключ принадлежит человеку. Поэтому и крошки
 * без проекта, и вход из меню пользователя, а не из основной навигации
 * (ADR-0045).
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { Check, Copy, ExternalLink } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorMessage } from '@/api/errors'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import ApiKeyDialog from '@/features/settings/integrations/components/ApiKeyDialog.vue'
import {
  listApiKeys,
  revokeApiKey,
  type ApiKey,
} from '@/features/settings/integrations/api'
import {
  connectCommand,
  formatExpiry,
  formatLastUsed,
  scopesLabel,
} from '@/features/settings/integrations/format'

const { t, locale } = useI18n()
const toast = useToast()

const keys = ref<ApiKey[]>([])
const activeLimit = ref(0)
const isLoading = ref(false)
const error = ref('')
const revokingId = ref('')
const isDialogOpen = ref(false)
const copied = ref(false)

/** Ссылка на репозиторий сервера: человек должен видеть, чему он доверяет. */
const REPO_URL = 'https://git.vpit.dev/vinprop/backend/tracker'
const REPO_LABEL = 'git.vpit.dev/vinprop/backend/tracker'

/** Порядок колонок блока «что агент делает»: читает, меняет, чего не может. */
const CAPABILITIES = ['reads', 'writes', 'cannot'] as const

const activeCount = computed(() => keys.value.filter((key) => key.isActive).length)
const canIssue = computed(() => activeLimit.value === 0 || activeCount.value < activeLimit.value)

/**
 * Про лимит договариваем только когда он известен: пока ключи не загрузились
 * (или ответ пришёл без поля), «До 0 активных» читается как «нельзя ни одного».
 */
const keysHint = computed(() =>
  activeLimit.value > 0
    ? t('settings.integrations.keys.hint', { limit: activeLimit.value })
    : t('settings.integrations.keys.hintNoLimit'),
)

/** Команда подключения с плейсхолдером вместо ключа: настоящий тут не покажешь. */
const command = computed(() => connectCommand())

const load = async () => {
  isLoading.value = true
  error.value = ''

  try {
    const page = await listApiKeys()
    keys.value = page.keys
    activeLimit.value = page.activeLimit
  } catch (err) {
    error.value = errorMessage(err, t('settings.integrations.errors.load'))
  } finally {
    isLoading.value = false
  }
}

const copyCommand = async () => {
  try {
    await navigator.clipboard.writeText(command.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch {
    toast.error(t('settings.integrations.errors.copy'))
  }
}

const revoke = async (key: ApiKey) => {
  revokingId.value = key.id

  try {
    await revokeApiKey(key.id)
    toast.success(t('settings.integrations.keys.revoked', { name: key.name }))
    await load()
  } catch (err) {
    toast.error(errorMessage(err, t('settings.integrations.errors.revoke')))
  } finally {
    revokingId.value = ''
  }
}

const onIssued = async () => {
  await load()
}

onMounted(load)
</script>

<template>
  <TrackerToolbar
    :breadcrumbs="[
      { label: t('settings.personal.title'), to: '/settings' },
      { label: t('settings.integrations.title') },
    ]"
  />

  <div class="integrations">
    <!-- Подключение: два шага по порядку — сперва ключ, потом команда под него -->
    <section class="section">
      <p class="section__label">{{ t('settings.integrations.connect.title') }}</p>

      <div class="card">
        <div class="step">
          <span class="step__num">1</span>

          <div class="step__text">
            <p class="step__title">{{ t('settings.integrations.connect.step1') }}</p>
            <p class="step__sub">{{ keysHint }}</p>
          </div>

          <AppButton size="small" :disabled="!canIssue" @click="isDialogOpen = true">
            {{ t('settings.integrations.keys.issue') }}
          </AppButton>
        </div>

        <div class="card__divider" />

        <div class="step step--stacked">
          <div class="step__head">
            <span class="step__num">2</span>

            <div class="step__text">
              <p class="step__title">{{ t('settings.integrations.connect.step2') }}</p>
              <p class="step__sub">{{ t('settings.integrations.connect.step2Hint') }}</p>
            </div>
          </div>

          <div class="command">
            <code class="command__code">{{ command }}</code>
            <AppButton variant="secondary" size="small" @click="copyCommand">
              <component :is="copied ? Check : Copy" :size="14" />
              {{ copied ? t('settings.integrations.connect.copied') : t('settings.integrations.connect.copy') }}
            </AppButton>
          </div>

          <a class="repo" :href="REPO_URL" target="_blank" rel="noreferrer noopener">
            <ExternalLink :size="14" />
            <span class="repo__link">{{ REPO_LABEL }}</span>
            <span class="repo__note">{{ t('settings.integrations.connect.repo') }}</span>
          </a>
        </div>
      </div>
    </section>

    <!-- Ключи доступа: живые и погашенные вперемешку, историей -->
    <section class="section">
      <p class="section__label">{{ t('settings.integrations.keys.title') }}</p>

      <p v-if="error" class="error">{{ error }}</p>

      <div v-else-if="!isLoading && keys.length === 0" class="card empty">
        <p class="empty__title">{{ t('settings.integrations.keys.empty') }}</p>
        <p class="empty__hint">{{ t('settings.integrations.keys.emptyHint') }}</p>
      </div>

      <div v-else class="card">
        <table class="keys">
          <thead>
            <tr>
              <th>{{ t('settings.integrations.keys.columns.name') }}</th>
              <th>{{ t('settings.integrations.keys.columns.access') }}</th>
              <th>{{ t('settings.integrations.keys.columns.lastUsed') }}</th>
              <th>{{ t('settings.integrations.keys.columns.expiry') }}</th>
              <th class="keys__actions-head"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="key in keys" :key="key.id" :class="{ 'keys__row--revoked': !key.isActive }">
              <td>
                <p class="keys__name">{{ key.name }}</p>
                <p class="keys__prefix">{{ key.keyPrefix }}…</p>
              </td>
              <td>{{ scopesLabel(key.scopes, t) }}</td>
              <td>{{ formatLastUsed(key.lastUsedAt, locale, t) }}</td>
              <td>{{ formatExpiry(key, locale, t) }}</td>
              <td class="keys__actions">
                <button
                  v-if="key.isActive"
                  class="keys__revoke"
                  :disabled="revokingId === key.id"
                  @click="revoke(key)"
                >
                  {{ t('settings.integrations.keys.revoke') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Что агент делает ключом: три колонки в одной карточке, последняя — запреты -->
    <section class="section">
      <p class="section__label">{{ t('settings.integrations.capabilities.title') }}</p>

      <div class="card caps">
        <div v-for="group in CAPABILITIES" :key="group" class="caps__col">
          <p class="caps__title" :class="{ 'caps__title--danger': group === 'cannot' }">
            {{ t(`settings.integrations.capabilities.${group}.title`) }}
          </p>

          <p v-for="line in 4" :key="line" class="caps__line">
            <span class="caps__glyph" :class="{ 'caps__glyph--danger': group === 'cannot' }">
              {{ group === 'cannot' ? '×' : '·' }}
            </span>
            {{ t(`settings.integrations.capabilities.${group}.items.${line - 1}`) }}
          </p>
        </div>
      </div>
    </section>
  </div>

  <ApiKeyDialog v-model:open="isDialogOpen" @issued="onIssued" />
</template>

<style scoped>
.integrations {
  padding: 28px 48px 48px;
}

.section + .section {
  margin-top: var(--spacing-6);
}

/* Микро-заголовок раздела: тот же приём, что на остальных экранах настроек */
.section__label {
  margin: 0 0 var(--spacing-2);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.card {
  background: var(--color-surface);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-xl);
}

.card__divider {
  height: 1px;
  background: var(--color-border-default);
}

/* ── подключение ───────────────────────────────────────────── */

.step {
  display: flex;
  gap: var(--spacing-4);
  align-items: center;
  padding: var(--spacing-4) var(--spacing-5);
}

.step--stacked {
  flex-direction: column;
  align-items: stretch;
  gap: var(--spacing-3);
}

.step__head {
  display: flex;
  gap: var(--spacing-4);
  align-items: center;
}

.step__num {
  display: flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  background: var(--color-primary-tint);
  border-radius: var(--radius-full);
}

.step__text {
  flex: 1;
  min-width: 0;
}

.step__title {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.step__sub {
  margin: 2px 0 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

.command {
  display: flex;
  gap: var(--spacing-3);
  align-items: center;
  padding: var(--spacing-3);
  background: var(--color-bg-muted);
  border-radius: var(--radius-sm);
}

.command__code {
  flex: 1;
  min-width: 0;
  font-family: var(--font-family-mono);
  font-size: var(--font-size-xs);
  line-height: 1.6;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-all;
}

.repo {
  display: inline-flex;
  gap: var(--spacing-2);
  align-items: center;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  text-decoration: none;
}

.repo__link {
  color: var(--color-primary);
}

.repo:hover .repo__link {
  text-decoration: underline;
}

/* ── ключи ─────────────────────────────────────────────────── */

.error {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-danger);
}

.empty {
  padding: 28px var(--spacing-5);
  text-align: center;
}

.empty__title {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.empty__hint {
  margin: var(--spacing-1) 0 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

.keys {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.keys th {
  padding: var(--spacing-2) var(--spacing-5);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-muted);
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--color-bg-muted);
}

.keys th:first-child {
  border-top-left-radius: var(--radius-xl);
}

.keys th:last-child {
  border-top-right-radius: var(--radius-xl);
}

.keys td {
  padding: var(--spacing-3) var(--spacing-5);
  color: var(--color-text-secondary);
  border-top: 1px solid var(--color-border-default);
}

.keys__name {
  margin: 0;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

/* Префикс живёт под именем: отдельная колонка на семь звёздочек не нужна */
.keys__prefix {
  margin: 2px 0 0;
  font-family: var(--font-family-mono);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

/* Погашенный ключ остаётся в списке историей, но читается как выключенный. */
.keys__row--revoked td,
.keys__row--revoked .keys__name {
  color: var(--color-text-muted);
}

.keys__actions {
  text-align: right;
}

.keys__revoke {
  padding: 0;
  font: inherit;
  font-weight: var(--font-weight-medium);
  color: var(--color-danger);
  cursor: pointer;
  background: none;
  border: none;
}

.keys__revoke:disabled {
  color: var(--color-text-muted);
  cursor: default;
}

/* ── что агент делает ──────────────────────────────────────── */

.caps {
  display: flex;
}

.caps__col {
  flex: 1;
  min-width: 0;
  padding: var(--spacing-4) var(--spacing-5);
}

.caps__col + .caps__col {
  border-left: 1px solid var(--color-border-default);
}

.caps__title {
  margin: 0 0 var(--spacing-2);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.caps__title--danger {
  color: var(--color-danger);
}

.caps__line {
  display: flex;
  gap: var(--spacing-2);
  margin: 0;
  font-size: var(--font-size-xs);
  line-height: 1.7;
  color: var(--color-text-secondary);
}

.caps__glyph {
  flex: 0 0 8px;
  color: var(--color-text-muted);
  text-align: center;
}

.caps__glyph--danger {
  color: var(--color-danger);
}
</style>
